import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { useStoreNavigation } from "../storeNavigation";
import type { TrackerCell, TrackerStatus } from "../types";

interface SectionTrackerProps {
  title: string;
  cells: TrackerCell[];
}

const STATUS_TONE: Record<TrackerStatus, string> = {
  ok: "bg-status-success/70",
  warn: "bg-status-warning/70",
  err: "bg-status-critical/70",
};

/**
 * Per-row binary/ternary health strip. One cell per axis tick; tooltip shows
 * the label + optional detail on hover. Useful for "passed N of M checks"
 * dashboards where the dimension is sequential (stores, weeks, surveys).
 * Cells carrying a store_number become real buttons that open the per-store
 * view when a store navigation context is present.
 */
export function SectionTracker({ cells }: SectionTrackerProps) {
  const nav = useStoreNavigation();
  return (
    <div className="px-5 py-4">
      <div className="flex flex-wrap gap-1.5">
        {cells.map((c, i) => {
          const storeNumber = c.store_number;
          const openStore =
            nav && storeNumber ? () => nav.openStore(storeNumber, c.label) : null;
          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                {openStore ? (
                  <button
                    type="button"
                    onClick={openStore}
                    className={cn(
                      "h-6 w-3.5 rounded-[2px] cursor-pointer",
                      "hover:ring-1 hover:ring-foreground/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                      STATUS_TONE[c.status],
                    )}
                    aria-label={`${c.label}: ${c.status}`}
                  />
                ) : (
                  <span
                    className={cn(
                      "h-6 w-3.5 rounded-[2px] cursor-default",
                      STATUS_TONE[c.status],
                    )}
                    aria-label={`${c.label}: ${c.status}`}
                  />
                )}
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <div className="font-medium">{c.label}</div>
                {c.detail ? (
                  <div className="text-muted-foreground">{c.detail}</div>
                ) : null}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
